import React from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, 
  Layers, 
  Zap, 
  GitBranch, 
  Database, 
  Lock, 
  Network, 
  Cpu,
  Binary,
  Code,
  Globe,
  ArrowRightLeft,
  Box,
  FileCode,
  Server,
  Activity
} from 'lucide-react';

const Technology = () => {
  const technologies = [
    {
      category: "Consensus Layer",
      items: [
        {
          name: "FlowConsensus",
          description: "Hybrid consensus mechanism combining intra-shard and inter-shard coordination",
          icon: <GitBranch size={32} />,
          stack: "Rust (tokio async)"
        },
        {
          name: "BlockDAG",
          description: "Directed Acyclic Graph for parallel block processing within shards",
          icon: <Network size={32} />,
          stack: "GHOSTDAG algorithm"
        },
        {
          name: "BFT Consensus",
          description: "Byzantine Fault Tolerant with 67% honest majority requirement",
          icon: <Shield size={32} />,
          stack: "2/3 + 1 quorum"
        }
      ]
    },
    {
      category: "Sharding & Scalability",
      items: [
        {
          name: "Dynamic Sharding",
          description: "64 parallel shards with automatic state management and cross-shard communication",
          icon: <Layers size={32} />,
          stack: "Shard count: 64"
        },
        {
          name: "Cross-Shard Protocol",
          description: "Two-Phase Commit (2PC) with ZK-SNARK proofs for atomic cross-shard transactions",
          icon: <ArrowRightLeft size={32} />,
          stack: "Groth16 ZK proofs"
        },
        {
          name: "VRF Coordinator",
          description: "Verifiable Random Function for fair shard assignment",
          icon: <Binary size={32} />,
          stack: "Schnorr VRF"
        }
      ]
    },
    {
      category: "Cryptography",
      items: [
        {
          name: "BLS Signatures",
          description: "BLS12-381 curve for signature aggregation with O(1) verification",
          icon: <Lock size={32} />,
          stack: "blst library"
        },
        {
          name: "Threshold Encryption",
          description: "Distributed key generation for MEV protection and fair ordering",
          icon: <Shield size={32} />,
          stack: "threshold_crypto"
        },
        {
          name: "ZK-SNARKs",
          description: "Zero-knowledge proofs for privacy and cross-shard validation",
          icon: <Code size={32} />,
          stack: "bellman (Groth16)"
        }
      ]
    },
    {
      category: "Storage & State",
      items: [
        {
          name: "RocksDB",
          description: "High-performance key-value store for blockchain state and history",
          icon: <Database size={32} />,
          stack: "LSM Tree"
        },
        {
          name: "Merkle Patricia Trie",
          description: "Ethereum-compatible state trie for efficient state proofs",
          icon: <GitBranch size={32} />,
          stack: "SHA3-256 hashing"
        },
        {
          name: "Write-Ahead Log",
          description: "WAL for crash recovery and data consistency",
          icon: <FileCode size={32} />,
          stack: "RocksDB WAL"
        }
      ]
    },
    {
      category: "Network Layer",
      items: [
        {
          name: "libp2p",
          description: "Modular peer-to-peer networking stack with multi-transport support",
          icon: <Globe size={32} />,
          stack: "rust-libp2p"
        },
        {
          name: "Gossipsub",
          description: "Pub/sub protocol for efficient message propagation across network",
          icon: <Network size={32} />,
          stack: "Mesh topology"
        },
        {
          name: "Kademlia DHT",
          description: "Distributed Hash Table for peer discovery and routing",
          icon: <Server size={32} />,
          stack: "XOR distance metric"
        }
      ]
    },
    {
      category: "Execution & VM",
      items: [
        {
          name: "REVM",
          description: "Rust Ethereum Virtual Machine for 100% EVM compatibility",
          icon: <Cpu size={32} />,
          stack: "revm 29.0.1"
        },
        {
          name: "EIP-1559 Gas",
          description: "Dynamic fee market with base fee and priority fee mechanism",
          icon: <Activity size={32} />,
          stack: "Burn 40% fees"
        },
        {
          name: "State Machine",
          description: "Transaction execution engine with atomic state transitions",
          icon: <Box size={32} />,
          stack: "ACID guarantees"
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-16 bg-black text-white">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <h1 className="text-6xl md:text-7xl font-bold mb-6 text-white">
            Technology Stack
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            AVO Protocol is built on cutting-edge technologies working together to deliver 
            ultra-high performance, security, and decentralization.
          </p>
        </motion.div>

        {/* Architecture Overview */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-20"
        >
          <div className="bg-black border-2 border-white p-12">
            <h2 className="text-4xl font-bold mb-8 text-center text-white">System Architecture</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border-2 border-white">
              <div className="border-r-2 border-white p-8 hover:bg-gray-950 transition-all group">
                <div className="mb-4">
                  <Layers size={40} className="mx-auto text-white group-hover:text-blue-400 transition-colors" />
                </div>
                <h3 className="text-2xl font-bold text-center mb-4 text-white">Layer 1</h3>
                <p className="text-center font-mono text-sm text-gray-300">Consensus</p>
                <p className="text-center text-xs mt-2 text-gray-500">FlowConsensus + BlockDAG</p>
              </div>
              
              <div className="border-r-2 border-white p-8 hover:bg-gray-950 transition-all group">
                <div className="mb-4">
                  <GitBranch size={40} className="mx-auto text-white group-hover:text-purple-400 transition-colors" />
                </div>
                <h3 className="text-2xl font-bold text-center mb-4 text-white">Layer 2</h3>
                <p className="text-center font-mono text-sm text-gray-300">Sharding</p>
                <p className="text-center text-xs mt-2 text-gray-500">64 Parallel Shards</p>
              </div>
              
              <div className="border-r-2 border-white p-8 hover:bg-gray-950 transition-all group">
                <div className="mb-4">
                  <Cpu size={40} className="mx-auto text-white group-hover:text-green-400 transition-colors" />
                </div>
                <h3 className="text-2xl font-bold text-center mb-4 text-white">Layer 3</h3>
                <p className="text-center font-mono text-sm text-gray-300">Execution</p>
                <p className="text-center text-xs mt-2 text-gray-500">REVM (EVM Compatible)</p>
              </div>
              
              <div className="p-8 hover:bg-gray-950 transition-all group">
                <div className="mb-4">
                  <Database size={40} className="mx-auto text-white group-hover:text-yellow-400 transition-colors" />
                </div>
                <h3 className="text-2xl font-bold text-center mb-4 text-white">Layer 4</h3>
                <p className="text-center font-mono text-sm text-gray-300">Persistence</p>
                <p className="text-center text-xs mt-2 text-gray-500">RocksDB + MPT</p>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Technology Grid */}
        {technologies.map((category, categoryIndex) => (
          <motion.section
            key={categoryIndex}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * categoryIndex }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold mb-8 border-b border-white pb-4">
              {category.category}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {category.items.map((tech, techIndex) => (
                <motion.div
                  key={techIndex}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.05 * techIndex }}
                  className="bg-black border-2 border-white p-8 hover:border-cyan-400 transition-all cursor-pointer group"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="flex-shrink-0 text-white group-hover:text-cyan-400 transition-colors">
                      {tech.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2 text-white">{tech.name}</h3>
                      <p className="text-sm text-gray-400 mb-3">{tech.description}</p>
                      <div className="inline-block bg-white text-black px-3 py-1 text-xs font-mono">
                        {tech.stack}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.section>
        ))}

        {/* Data Flow Diagram */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold mb-8 border-b border-white pb-4">
            Transaction Flow
          </h2>
          
          <div className="bg-black border-2 border-white p-12">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="text-center">
                <div className="w-24 h-24 border-4 border-white flex items-center justify-center mx-auto mb-4 hover:border-blue-400 transition-colors">
                  <Globe size={40} className="text-white" />
                </div>
                <p className="font-bold text-lg text-white">1. User Submits TX</p>
                <p className="text-sm text-gray-400">via RPC endpoint</p>
              </div>
              
              <div className="text-4xl text-white">→</div>
              
              <div className="text-center">
                <div className="w-24 h-24 border-4 border-white flex items-center justify-center mx-auto mb-4 hover:border-purple-400 transition-colors">
                  <Binary size={40} className="text-white" />
                </div>
                <p className="font-bold text-lg text-white">2. VRF Assignment</p>
                <p className="text-sm text-gray-400">to target shard</p>
              </div>
              
              <div className="text-4xl text-white">→</div>
              
              <div className="text-center">
                <div className="w-24 h-24 border-4 border-white flex items-center justify-center mx-auto mb-4 hover:border-yellow-400 transition-colors">
                  <Lock size={40} className="text-white" />
                </div>
                <p className="font-bold text-lg text-white">3. Threshold Encrypt</p>
                <p className="text-sm text-gray-400">MEV protection</p>
              </div>
              
              <div className="text-4xl text-white">→</div>
              
              <div className="text-center">
                <div className="w-24 h-24 border-4 border-white flex items-center justify-center mx-auto mb-4 hover:border-green-400 transition-colors">
                  <Network size={40} className="text-white" />
                </div>
                <p className="font-bold text-lg text-white">4. DAG Consensus</p>
                <p className="text-sm text-gray-400">parallel ordering</p>
              </div>
              
              <div className="text-4xl text-white">→</div>
              
              <div className="text-center">
                <div className="w-24 h-24 border-4 border-white flex items-center justify-center mx-auto mb-4 hover:border-cyan-400 transition-colors">
                  <Cpu size={40} className="text-white" />
                </div>
                <p className="font-bold text-lg text-white">5. REVM Execute</p>
                <p className="text-sm text-gray-400">state transition</p>
              </div>
              
              <div className="text-4xl text-white">→</div>
              
              <div className="text-center">
                <div className="w-24 h-24 border-4 border-white flex items-center justify-center mx-auto mb-4 hover:border-pink-400 transition-colors">
                  <Database size={40} className="text-white" />
                </div>
                <p className="font-bold text-lg text-white">6. Commit State</p>
                <p className="text-sm text-gray-400">to RocksDB</p>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Performance Metrics */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold mb-8 border-b border-white pb-4">
            Performance Metrics
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-white">
            <div className="border-r border-b border-white p-8 text-center">
              <p className="text-4xl font-bold mb-2">520k+</p>
              <p className="text-sm text-gray-400">Transactions/Second</p>
            </div>
            <div className="border-r border-b border-white p-8 text-center">
              <p className="text-4xl font-bold mb-2">&lt;1s</p>
              <p className="text-sm text-gray-400">Finality Time</p>
            </div>
            <div className="border-r border-b border-white p-8 text-center">
              <p className="text-4xl font-bold mb-2">$0.001</p>
              <p className="text-sm text-gray-400">Average Fee</p>
            </div>
            <div className="border-b border-white p-8 text-center">
              <p className="text-4xl font-bold mb-2">99.99%</p>
              <p className="text-sm text-gray-400">Uptime Target</p>
            </div>
            <div className="border-r border-white p-8 text-center">
              <p className="text-4xl font-bold mb-2">64</p>
              <p className="text-sm text-gray-400">Parallel Shards</p>
            </div>
            <div className="border-r border-white p-8 text-center">
              <p className="text-4xl font-bold mb-2">O(1)</p>
              <p className="text-sm text-gray-400">BLS Verification</p>
            </div>
            <div className="border-r border-white p-8 text-center">
              <p className="text-4xl font-bold mb-2">100%</p>
              <p className="text-sm text-gray-400">EVM Compatible</p>
            </div>
            <div className="p-8 text-center">
              <p className="text-4xl font-bold mb-2">1000+</p>
              <p className="text-sm text-gray-400">Validator Target</p>
            </div>
          </div>
        </motion.section>

        {/* Tech Stack Summary */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="bg-black border-2 border-white p-12"
        >
          <h2 className="text-4xl font-bold mb-8 text-center text-white">Built With</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center border-2 border-white p-4 hover:border-blue-400 transition-colors">
              <p className="font-bold text-lg mb-2 text-white">Language</p>
              <p className="font-mono text-sm text-gray-300">Rust</p>
            </div>
            <div className="text-center border-2 border-white p-4 hover:border-purple-400 transition-colors">
              <p className="font-bold text-lg mb-2 text-white">Async Runtime</p>
              <p className="font-mono text-sm text-gray-300">Tokio</p>
            </div>
            <div className="text-center border-2 border-white p-4 hover:border-green-400 transition-colors">
              <p className="font-bold text-lg mb-2 text-white">Networking</p>
              <p className="font-mono text-sm text-gray-300">libp2p</p>
            </div>
            <div className="text-center border-2 border-white p-4 hover:border-yellow-400 transition-colors">
              <p className="font-bold text-lg mb-2 text-white">Storage</p>
              <p className="font-mono text-sm text-gray-300">RocksDB</p>
            </div>
            <div className="text-center border-2 border-white p-4 hover:border-cyan-400 transition-colors">
              <p className="font-bold text-lg mb-2 text-white">Crypto</p>
              <p className="font-mono text-sm text-gray-300">blst + bellman</p>
            </div>
            <div className="text-center border-2 border-white p-4 hover:border-pink-400 transition-colors">
              <p className="font-bold text-lg mb-2 text-white">EVM</p>
              <p className="font-mono text-sm text-gray-300">REVM 29.0</p>
            </div>
            <div className="text-center border-2 border-white p-4 hover:border-orange-400 transition-colors">
              <p className="font-bold text-lg mb-2 text-white">Serialization</p>
              <p className="font-mono text-sm text-gray-300">serde + bincode</p>
            </div>
            <div className="text-center border-2 border-white p-4 hover:border-red-400 transition-colors">
              <p className="font-bold text-lg mb-2 text-white">Hashing</p>
              <p className="font-mono text-sm text-gray-300">SHA3-256</p>
            </div>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default Technology;
